import { useMemo, useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import { routes } from 'config/routes'
import {
	ActionsSelect,
	BookingEngineTabs,
	CardHeader,
	CardWrapper,
	CtaLink,
	DeleteConfirmModal,
	EmptyTable,
	Header,
	SearchInput
} from 'shared/components'
import { useDevice, useModal, useTableSelect } from 'shared/hooks'
import { matchesSearch } from 'utils/matchesSearch'
import { useActionFilters } from './hooks/useActionFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useDeleteAreaTypeMutation } from './mutations/useDeleteAreaTypeMutation'
import { useDeleteAreaTypesMutation } from './mutations/useDeleteAreaTypesMutation'
import { useFetchAreaTypes } from './queries/useFetchAreaTypes'
import { TableSection } from './organisms/TableSection'

const AreaTypesPage = () => {
	const { t } = useTranslation()
	const { isMobileOrTablet } = useDevice()
	const [search, setSearch] = useState('')
	const [checkedAreaTypes, setCheckedAreaTypes] = useState<number[]>([])
	const [idToBeRemoved, setIdToBeRemoved] = useState<number | null>(null)
	const { boothTypes, isAreaTypesLoading } = useFetchAreaTypes()
	const results = useMemo(
		() =>
			(boothTypes?.data?.results ?? []).filter((boothType) =>
				matchesSearch(search, [boothType.name, boothType.description])
			),
		[boothTypes, search]
	)
	const { select } = useTableSelect({ nodes: results }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const clearSelection = () => select.fns.onRemoveAll()
	const [actionsFilter] = useActionFilters(!checkedAreaTypes.length)

	const {
		isOpen: isDeleteModalOpen,
		openModal: openDeleteModal,
		closeModal: closeDeleteModal
	} = useModal()
	const {
		isOpen: isBulkDeleteModalOpen,
		openModal: openBulkDeleteModal,
		closeModal: closeBulkDeleteModal
	} = useModal()

	const { deleteAreaTypeMutation } = useDeleteAreaTypeMutation({ closeDeleteModal })

	const { boothTypesDeleteMutation } = useDeleteAreaTypesMutation({
		onSettled: () => {
			clearSelection()
			closeBulkDeleteModal()
		}
	})

	function onSelectChange(_action: any, state: any) {
		setCheckedAreaTypes(state.ids)
	}

	const handleDelete = (id: number) => {
		setIdToBeRemoved(id)
		openDeleteModal()
	}

	const menuOptions = useContextMenuOptions({ onDelete: handleDelete })

	const handleAction = (action: string) => {
		switch (action) {
			case 'delete':
				openBulkDeleteModal()
				break
		}
	}

	const renderContent = () => {
		if (isAreaTypesLoading) return <EmptyTable isLoading />
		if (!boothTypes || !results.length)
			return <EmptyTable text={search ? t('No results') : t('No area types yet')} />

		return (
			<TableSection
				boothTypes={boothTypes}
				results={results}
				select={select}
				menuOptions={menuOptions}
			/>
		)
	}

	return (
		<>
			<Header
				title={t('Booking engine')}
				tabs={<BookingEngineTabs />}
				actions={
					!isMobileOrTablet && (
						<ActionsSelect options={actionsFilter.options} onChange={handleAction} />
					)
				}
			/>
			<div className="px-5 lg:flex lg:max-h-[calc(100vh-113.5px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<CardHeader title={t('Area types')} description={t('Types of areas that can be booked.')}>
						<SearchInput
							variant="filled"
							className="sm:w-64"
							label={t('Search types')}
							placeholder={t('Search types')}
							onChange={(event) => setSearch(event.target.value)}
						/>
						<CtaLink to={routes.areaTypeAdd} text={t('Add type')} />
					</CardHeader>
					{renderContent()}
				</CardWrapper>
			</div>

			{isDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete area type')}
					isOpened={isDeleteModalOpen}
					closeModal={closeDeleteModal}
					confirm={() => deleteAreaTypeMutation.mutate(idToBeRemoved as number)}>
					<p>{t('You are about to delete an area type. Are you sure you want to proceed?')}</p>
				</DeleteConfirmModal>
			)}
			{isBulkDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete selected area types')}
					isOpened={isBulkDeleteModalOpen}
					closeModal={closeBulkDeleteModal}
					confirm={() => boothTypesDeleteMutation({ ids: checkedAreaTypes })}>
					<p>
						{t(
							'You are about to delete {{ count }} area types. Are you sure you want to proceed?',
							{ count: checkedAreaTypes.length }
						)}
					</p>
				</DeleteConfirmModal>
			)}
		</>
	)
}

export default AreaTypesPage
